import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { OptionType } from '@smartsheet/lodestar-core';
import i18n from '../../i18n';
import {
  Schedule,
  getSchedules,
  upsertScheduleService,
  deleteSchedule,
  UpsertScheduleType,
  deleteScheduleType,
} from '../../services/api/SchedulesService';
import { switchBackToLegacyViewWithError } from '../../utils/helpers';
import { StoreState } from '../store/store';

export const fetchSchedules = createAsyncThunk<
  Schedule[],
  string,
  {
    state: StoreState;
  }
>('schedules/getSchedules', async workflowUUID => {
  const data = await getSchedules(workflowUUID);
  return data;
});

export interface deleteScheduleDataType {
  data: deleteScheduleType;
  scheduleID: string;
}

export const deleteScheduleData = createAsyncThunk<
  deleteScheduleDataType,
  string,
  {
    state: StoreState;
  }
>('schedules/deleteSchedule', async scheduleID => {
  const data = await deleteSchedule(scheduleID);
  return { data, scheduleID };
});

export const upsertScheduleData = createAsyncThunk<
  Schedule,
  UpsertScheduleType,
  {
    state: StoreState;
  }
>('schedules/upsertSchedule', async schedule => {
  const data = await upsertScheduleService(schedule);
  return data;
});

export interface Option extends OptionType {
  label: string;
  value: string;
}

export interface SchedulesState {
  schedules: Schedule[];
  selectedScheduleIndex: number | undefined;
  isUpsertScheduleVisible: boolean;
  RUN_OPTIONS: Option[];
  UNIT_OPTIONS: Option[];
}

const initialState: SchedulesState = {
  schedules: [],
  selectedScheduleIndex: undefined,
  isUpsertScheduleVisible: false,
  RUN_OPTIONS: [
    {
      label: i18n.t('designer:triggerPanel:schedules:runOptions:once'),
      value: '@once',
    },
    {
      label: i18n.t('designer:triggerPanel:schedules:runOptions:hourly'),
      value: '@every 1 hours',
    },
    {
      label: i18n.t('designer:triggerPanel:schedules:runOptions:daily'),
      value: '@every 1 days',
    },
    {
      label: i18n.t('designer:triggerPanel:schedules:runOptions:weekly'),
      value: '@every 1 weeks',
    },
    {
      label: i18n.t('designer:triggerPanel:schedules:runOptions:monthly'),
      value: '@every 1 months',
    },
    {
      label: i18n.t('designer:triggerPanel:schedules:runOptions:yearly'),
      value: '@every 1 years',
    },
    {
      label: i18n.t('designer:triggerPanel:schedules:runOptions:custom'),
      value: 'Custom',
    },
  ],
  UNIT_OPTIONS: [
    {
      label: i18n.t('designer:triggerPanel:schedules:unitOptions:minutes'),
      value: 'minutes',
    },
    {
      label: i18n.t('designer:triggerPanel:schedules:unitOptions:hours'),
      value: 'hours',
    },
    {
      label: i18n.t('designer:triggerPanel:schedules:unitOptions:days'),
      value: 'days',
    },
    {
      label: i18n.t('designer:triggerPanel:schedules:unitOptions:weeks'),
      value: 'weeks',
    },
    {
      label: i18n.t('designer:triggerPanel:schedules:unitOptions:months'),
      value: 'months',
    },
    {
      label: i18n.t('designer:triggerPanel:schedules:unitOptions:years'),
      value: 'years',
    },
  ],
};

const SchedulesSlice = createSlice({
  name: 'provider',
  initialState,
  reducers: {
    setSelectedScheduleIndex: (
      state,
      action: PayloadAction<SchedulesState['selectedScheduleIndex']>
    ) => {
      state.selectedScheduleIndex = action.payload;
    },
    setIsUpsertScheduleVisible: (
      state,
      action: PayloadAction<SchedulesState['isUpsertScheduleVisible']>
    ) => {
      state.isUpsertScheduleVisible = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchSchedules.rejected, (state, action) => {
      switchBackToLegacyViewWithError(action.error);
    });
    builder.addCase(fetchSchedules.fulfilled, (state, action) => {
      state.schedules = action.payload;
    });

    builder.addCase(upsertScheduleData.rejected, (state, action) => {
      switchBackToLegacyViewWithError(action.error);
    });
    builder.addCase(upsertScheduleData.fulfilled, (state, action) => {
      for (let i = 0; i < state.schedules.length; i++) {
        if (state.schedules[i].id === action.payload.id) {
          state.schedules[i] = action.payload;
          return;
        }
      }
      state.schedules.push(action.payload);
    });

    builder.addCase(deleteScheduleData.fulfilled, (state, action) => {
      state.schedules = state.schedules.filter(
        schedule => schedule.id !== action.payload.scheduleID
      );
    });

    builder.addCase(deleteScheduleData.rejected, (state, action) => {
      switchBackToLegacyViewWithError(action.error);
    });
  },
});

export const selectSchedules = (state: StoreState) => {
  return state.schedules.schedules;
};

export const selectUnitOptions = (state: StoreState) => {
  return state.schedules.UNIT_OPTIONS;
};

export const selectRunOptions = (state: StoreState) => {
  return state.schedules.RUN_OPTIONS;
};

export const selectSelectedScheduleIndex = (state: StoreState) => {
  return state.schedules.selectedScheduleIndex;
};

export const selectIsUpsertScheduleVisible = (state: StoreState) => {
  return state.schedules.isUpsertScheduleVisible;
};

export const { setSelectedScheduleIndex, setIsUpsertScheduleVisible } =
  SchedulesSlice.actions;
export default SchedulesSlice.reducer;
